import {
  User,
  UserCreate,
  UserUpdate,
  UserProfile,
  UserProfileUpdate,
  ProfileData,
  ProfileMinimalResponse,
} from "../types/api";
import { WORKER_API_URL } from "./config"; // Use WORKER_API_URL
import { getStoredToken } from "../stores/authStore";

const API_V1_STR = "/api/v1"; // FastAPI specific prefix for these new routes
const BASE_URL = `${WORKER_API_URL}${API_V1_STR}`;

const getAuthToken = (): string | null => {
  // Prefer token from auth store (Zustand) to avoid localStorage race/stale values
  try {
    const fromStore = getStoredToken();
    if (fromStore) return fromStore;
  } catch {}

  // Fallback to localStorage for legacy paths
  if (typeof window !== "undefined") {
    try {
      return localStorage.getItem("token");
    } catch {}
  }
  return null;
};

const getHeaders = (includeAuth = true): HeadersInit => {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };
  if (includeAuth) {
    const token = getAuthToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  }
  return headers;
};

// User CRUD operations
export const userService = {
  async createUser(userData: UserCreate): Promise<User> {
    const response = await fetch(`${BASE_URL}/users/`, {
      method: "POST",
      headers: getHeaders(false), // User creation might not need auth if it's a public signup
      body: JSON.stringify(userData),
    });
    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ detail: response.statusText }));
      throw new Error(errorData.detail || "Failed to create user");
    }
    return response.json();
  },

  async getCurrentUser(): Promise<User> {
    console.log("getCurrentUser: Fetching current user data");
    try {
      // Create a URL object to use searchParams
      const url = new URL(`${BASE_URL}/users/me`);
      url.searchParams.set("bypassCache", "true");
      
      const response = await fetch(url.toString(), {
        headers: getHeaders(),
      });
      
      console.log(`getCurrentUser: Response status: ${response.status}`);
      
      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ detail: response.statusText }));
        console.error("getCurrentUser: Error response:", errorData);
        throw new Error(errorData.detail || "Failed to get current user");
      }
      
      const userData = await response.json();
      console.log("getCurrentUser: User data received:", userData);
      return userData;
    } catch (error) {
      console.error("getCurrentUser: Exception fetching user:", error);
      throw error;
    }
  },

  async updateUserMe(userData: UserUpdate): Promise<User> {
    const response = await fetch(`${BASE_URL}/users/me`, {
      method: "PATCH",
      headers: getHeaders(),
      body: JSON.stringify(userData),
    });
    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ detail: response.statusText }));
      throw new Error(errorData.detail || "Failed to update user");
    }
    return response.json();
  },

  async getUserById(userId: number): Promise<User> {
    const response = await fetch(
      `${BASE_URL}/users/${userId}`,
      { headers: getHeaders() } // Consider if this needs auth
    );
    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ detail: response.statusText }));
      throw new Error(errorData.detail || "User not found");
    }
    return response.json();
  },

  async updateUser(userId: number, userData: UserUpdate): Promise<User> {
    const response = await fetch(`${BASE_URL}/users/${userId}`, {
      method: "PUT",
      headers: getHeaders(), // Typically admin-only, needs auth
      body: JSON.stringify(userData),
    });
    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ detail: response.statusText }));
      throw new Error(errorData.detail || "Failed to update user");
    }
    return response.json();
  },

  async getUsers(skip: number = 0, limit: number = 100): Promise<User[]> {
    const response = await fetch(
      `${BASE_URL}/users/?skip=${skip}&limit=${limit}`,
      { headers: getHeaders() } // Typically admin-only, needs auth
    );
    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ detail: response.statusText }));
      throw new Error(errorData.detail || "Failed to retrieve users");
    }
    return response.json();
  },

  async deleteUser(userId: number): Promise<User> {
    const response = await fetch(`${BASE_URL}/users/${userId}`, {
      method: "DELETE",
      headers: getHeaders(), // Typically admin-only, needs auth
    });
    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ detail: response.statusText }));
      throw new Error(errorData.detail || "Failed to delete user");
    }
    return response.json();
  },

  // Get user profile data from the user object (profile is embedded in /auth/me response)
  async getUserProfile(userId: number): Promise<UserProfile | null> {
    console.log(`getUserProfile: Fetching profile for user ID ${userId} via current user`);
    try {
      // Get current user data which includes embedded profile
      const userData = await this.getCurrentUser();
      
      if (userData.user_id !== userId) {
        console.warn(`getUserProfile: User ID mismatch. Current user: ${userData.user_id}, requested: ${userId}`);
        throw new Error("Cannot access profile for different user");
      }
      
      console.log("getUserProfile: Profile data from user object:", userData.profile);
      return userData.profile || null;
    } catch (error) {
      console.error("getUserProfile: Exception fetching profile:", error);
      throw error;
    }
  },

  async updateUserProfile(
    userId: number,
    profileData: UserProfileUpdate
  ): Promise<UserProfile> {
    console.log(`updateUserProfile: Updating profile for user ID ${userId} via auth endpoint`);
    
    // Verify user is updating their own profile
    const currentUser = await this.getCurrentUser();
    if (currentUser.user_id !== userId) {
      throw new Error("Cannot update profile for different user");
    }
    
    // Use the profile update endpoint which handles both user and profile data
    const response = await fetch(`${WORKER_API_URL}/profile/update`, {
      method: "PATCH",
      headers: getHeaders(),
      body: JSON.stringify(profileData),
    });
    
    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ detail: response.statusText }));
      throw new Error(errorData.detail || "Failed to update user profile");
    }
    
    const updatedUser = await response.json();
    return updatedUser.profile || null;
  },

  // Complete user profile questionnaire - accepts already-transformed UserUpdate data
  async completeUserProfile(
    userUpdateData: any
  ): Promise<any> {
    console.log("completeUserProfile: Sending data to worker:", userUpdateData);
    
    // Warm the Worker token cache to avoid 401 'Invalid token' if this is the first Worker call
    try {
      const warmUrl = new URL(`${WORKER_API_URL}/profile/me`);
      warmUrl.searchParams.set("bypassCache", "true");
      await fetch(warmUrl.toString(), {
        headers: getHeaders(),
      });
    } catch (e) {
      // Non-blocking best-effort cache warm; proceed regardless
      console.warn("completeUserProfile: cache warm attempt failed (non-blocking)", e);
    }
    
    const response = await fetch(`${WORKER_API_URL}/profile/update`, {
      method: "PATCH",
      headers: getHeaders(),
      body: JSON.stringify(userUpdateData),
    });
    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ detail: response.statusText }));
      const detail =
        errorData.error ||
        errorData.detail ||
        "Failed to complete user profile";
      throw new Error(detail);
    }
    return response.json();
  },
};
